import React from 'react';
import { ChevronDownIcon, ChevronUpIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  AUTO_PARSE_ACTION_LABELS,
  AUTO_PARSE_BUCKETS,
  AutoParseAction,
} from '../utils/responseAutoParse';

interface ResponseAutoParseStartModalProps {
  open: boolean;
  loadingTemplates: boolean;
  config: Record<string, AutoParseAction>;
  counts: Record<string, number>;
  parseLimit: number;
  availableCount: number;
  inviteMessage: string;
  rejectMessage: string;
  onClose: () => void;
  onApply: () => void;
  onParseLimitChange: (value: number) => void;
  onConfigChange: (bucketId: string, action: AutoParseAction) => void;
  onInviteMessageChange: (value: string) => void;
  onRejectMessageChange: (value: string) => void;
}

const ResponseAutoParseStartModal: React.FC<ResponseAutoParseStartModalProps> = ({
  open,
  loadingTemplates,
  config,
  counts,
  parseLimit,
  availableCount,
  inviteMessage,
  rejectMessage,
  onClose,
  onApply,
  onParseLimitChange,
  onConfigChange,
  onInviteMessageChange,
  onRejectMessageChange,
}) => {
  if (!open) return null;

  const maxLimit = Math.max(1, Math.min(500, availableCount || 1));
  const normalizedLimit = Math.min(maxLimit, Math.max(1, parseLimit || 1));
  const changeLimit = (value: number) => {
    onParseLimitChange(Math.min(maxLimit, Math.max(1, Math.floor(value || 1))));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Авторазбор откликов</h3>
            <p className="text-sm text-gray-500 mt-1">Проверьте действия по категориям и тексты HH.ru перед запуском.</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1 space-y-6">
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm font-medium text-[#0A0D14]" htmlFor="auto-parse-limit">
              Разобрать откликов
            </label>
            <div className="relative h-10 w-24 rounded-xl border border-[#E2E4E9] bg-white">
              <input
                id="auto-parse-limit"
                type="number"
                min={1}
                max={maxLimit}
                value={normalizedLimit}
                onChange={(event) => changeLimit(parseInt(event.target.value || '1', 10))}
                disabled={loadingTemplates}
                className="h-full w-full rounded-xl border-0 bg-transparent pl-3 pr-8 text-sm text-[#0A0D14] text-center no-spinner focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed"
              />
              <div className="absolute right-1 top-1 bottom-1 flex w-6 flex-col overflow-hidden rounded-lg border border-[#E2E4E9] bg-[#F6F8FA]">
                <button
                  type="button"
                  onClick={() => changeLimit(normalizedLimit + 1)}
                  disabled={loadingTemplates || normalizedLimit >= maxLimit}
                  className="flex h-1/2 items-center justify-center text-[#525866] hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="Увеличить количество откликов"
                >
                  <ChevronUpIcon className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  onClick={() => changeLimit(normalizedLimit - 1)}
                  disabled={loadingTemplates || normalizedLimit <= 1}
                  className="flex h-1/2 items-center justify-center border-t border-[#E2E4E9] text-[#525866] hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="Уменьшить количество откликов"
                >
                  <ChevronDownIcon className="h-3 w-3" />
                </button>
              </div>
            </div>
            <span className="text-xs text-[#868C98]">доступно {availableCount}</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {AUTO_PARSE_BUCKETS.map((bucket) => (
              <div key={bucket.id} className="border border-[#E2E4E9] rounded-xl p-3 flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-[#0A0D14]">{bucket.label}</div>
                  <div className="text-xs text-[#868C98]">{counts[bucket.id] || 0} откликов</div>
                </div>
                <select
                  value={config[bucket.id] || bucket.defaultAction}
                  onChange={(event) => onConfigChange(bucket.id, event.target.value as AutoParseAction)}
                  className="h-9 min-w-[150px] px-3 rounded-xl border border-[#E2E4E9] bg-white text-sm text-[#0A0D14] focus:outline-none focus:ring-2 focus:ring-[#E16349]"
                >
                  {(['invite', 'clarify', 'reject'] as AutoParseAction[]).map((action) => (
                    <option key={action} value={action}>
                      {AUTO_PARSE_ACTION_LABELS[action]}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">Шаблон “Пригласить”</label>
                <span className="text-xs text-gray-400">HH.ru</span>
              </div>
              <textarea
                value={inviteMessage}
                onChange={(event) => onInviteMessageChange(event.target.value)}
                disabled={loadingTemplates}
                placeholder={loadingTemplates ? 'Загрузка шаблона...' : 'Текст приглашения'}
                className="w-full h-32 px-4 py-3 border border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-[#E16349] focus:border-transparent text-sm"
              />
              <div className="text-right text-xs text-gray-400 mt-1">{inviteMessage.length} символов</div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">Шаблон “Отказать”</label>
                <span className="text-xs text-gray-400">HH.ru</span>
              </div>
              <textarea
                value={rejectMessage}
                onChange={(event) => onRejectMessageChange(event.target.value)}
                disabled={loadingTemplates}
                placeholder={loadingTemplates ? 'Загрузка шаблона...' : 'Текст отказа'}
                className="w-full h-32 px-4 py-3 border border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-[#E16349] focus:border-transparent text-sm"
              />
              <div className="text-right text-xs text-gray-400 mt-1">{rejectMessage.length} символов</div>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 bg-gray-50 flex items-center justify-end">
          <button
            type="button"
            onClick={onApply}
            disabled={loadingTemplates}
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#E16349] text-white font-medium rounded-xl hover:bg-[#c9573f] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Применить и запустить
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResponseAutoParseStartModal;
